import { schema, types } from 'papr'
import { CUSTOM_DOMAIN_STATUS } from './docs-project'

export const EMAIL_IMPORT_STATUS = [
	// The process is initialized from the API but not worked on async yet
	'INITIALIZED' as const,
	// The process is in progress and a background worker is working on it
	'IN_PROGRESS' as const,
	// The process is in review -> Over a certain threshold
	'IN_REVIEW' as const,
	// has been rejected by the reviewer
	'REJECTED' as const,
	// The process failed
	'FAILED' as const,
	// The process succeeded
	'SUCCESS' as const,
	// The process is finished -> Only a new process can be launched from here
	'FINISHED' as const,
]

export type EmailImportStatus = (typeof EMAIL_IMPORT_STATUS)[number]

const navbarItemType = types.object({
	_id: types.objectId({ required: true }),
	type: types.enum(['link' as const, 'page' as const, 'series' as const], {
		required: true,
	}),
	label: types.string({ required: true }),
	url: types.string(),
	page: types.objectId(),
	series: types.objectId(),
	priority: types.number(),
})

const pageRuleType = types.object({
	_id: types.objectId({ required: true }),
	type: types.number({ required: true }),
	path: types.string({ required: true }),
	destination: types.string({ required: true }),
})

export type NavbarItem = typeof navbarItemType

export type PageRule = typeof pageRuleType

export const publicationSchema = schema(
	{
		title: types.string(),
		// will not be there on deleted publications
		author: types.objectId({ required: true }),
		isTeam: types.boolean(),
		domain: types.string(),
		username: types.string(),
		favicon: types.string(),
		logo: types.string(),
		darkModeLogo: types.string(),
		ogImage: types.string(),
		meta: types.string(),
		metaHTML: types.string(),
		metaAdmin: types.string(),
		imprintMarkdown: types.string(),
		imprint: types.string(),
		domainUpdatedOn: types.date(),
		domainVerificationExecutionArn: types.string(),
		domainStatus: types.object({
			certIssued: types.boolean(),
			ready: types.boolean(),
			status: types.enum(CUSTOM_DOMAIN_STATUS),
			verifiedAt: types.date(),
		}),
		wwwPrefixedDomain: types.string(),
		wwwPrefixedDomainStatus: types.object({
			certIssued: types.boolean(),
			ready: types.boolean(),
			status: types.enum(CUSTOM_DOMAIN_STATUS),
			verifiedAt: types.date(),
		}),
		isActive: types.boolean(),
		urlPattern: types.string(),
		wmPaymentPointer: types.string(),
		links: types.object({
			twitter: types.string(),
			instagram: types.string(),
			github: types.string(),
			website: types.string(),
			hashnode: types.string(),
			youtube: types.string(),
			dailydev: types.string(),
			linkedin: types.string(),
			mastodon: types.string(),
			facebook: types.string(),
			bluesky: types.string(),
		}),
		isAMPEnabled: types.boolean(),
		darkModeEnabled: types.boolean(),
		displayTitle: types.string(),
		description: types.string(),
		headerColor: types.string(),
		gaTrackingID: types.string(),
		gTagManagerID: types.string(),
		fathomSiteID: types.string(),
		fathomCustomDomain: types.string(),
		fathomCustomDomainEnabled: types.boolean(),
		hotjarSiteID: types.string(),
		matomoURL: types.string(),
		matomoSiteID: types.number(),
		fbPixelID: types.string(),
		msClarityID: types.string(),
		koalaPublicKey: types.string(),
		/**
		 * Publication that has been manually verified by Hashnode Team.
		 *
		 * Used to enable certain special actions for the publication.
		 */
		verified: types.boolean(),
		/**
		 * The number of followers of this publication.
		 *
		 * This is only set for team publications.
		 */
		numFollowers: types.number(),
		plausibleAnalyticsEnabled: types.boolean(),
		domainValidations: types.object({
			sslArn: types.string(),
			sslCNAME: types.string(),
			sslCNAMEValue: types.string(),
			cloudfrontDomain: types.string(),
		}),
		sitemapSubmitted: types.boolean(),
		tweetedAboutBlog: types.boolean(),
		badgePageEnabled: types.boolean(),
		membersPageEnabled: types.boolean(),
		hasBadges: types.boolean(),
		metaTags: types.string(),
		embedCode: types.string(),
		layout: types.enum(['stacked' as const, 'grid' as const, 'magazine' as const]),
		newsletterEnabled: types.boolean(),
		newsletterPageEnabled: types.boolean(),
		textSelectionSharerEnabled: types.boolean(),
		/**
		 * Signifies if role-based invite links are active. Users can only join via the link if this is true.
		 */
		areRoleBasedInviteLinksActive: types.boolean(),
		newsletterFrequency: types.enum(['asap' as const, 'weekly' as const]),
		isFeatured: types.boolean(),
		dateFeatured: types.date(),
		dailyViewsForCurrentMonth: types.objectGeneric(types.number()),
		betaFeatures: types.objectGeneric(types.boolean()),
		/**
		 * Views for different time periods.
		 *
		 * Not realtime but updated periodically based on Timestream data.
		 */
		views: types.object({
			/**
			 * Total views in the last 7 days.
			 */
			thisWeek: types.number(),
			/**
			 * Total views in the last 30 days.
			 */
			thisMonth: types.number(), // actually last 30 days
			/**
			 * Total views without any time restriction.
			 */
			allTime: types.number(),
		}),
		viewCountVisible: types.boolean(),
		readTimeHidden: types.boolean(),
		rssIntegration: types.objectId(),
		customCSSEnabled: types.boolean(),
		customCSSPublished: types.object({
			home: types.string(),
			homeMin: types.string(),
			post: types.string(),
			postMin: types.string(),
			static: types.string(),
			staticMin: types.string(),
		}),
		customCSSDraft: types.object({
			home: types.string(),
			homeMin: types.string(),
			post: types.string(),
			postMin: types.string(),
			static: types.string(),
			staticMin: types.string(),
		}),
		customRules: types.array(pageRuleType),
		domainChecklistSkipped: types.boolean(),
		menu: types.array(navbarItemType),
		stripe: types.object({
			connected: types.boolean(),
			accountId: types.string(),
			country: types.string(),
		}),
		sponsorship: types.object({
			contentMarkdown: types.string(),
			content: types.string(),
		}),
		teamBulkInviteToken: types.string(),
		// TODO: ?
		pageViews2021: types.number(),
		// TODO: ?
		isEligibleFor2021EoyReview: types.boolean(),
		umamiDataImported: types.boolean(),
		umamiWebsiteUUID: types.string(),
		umamiShareId: types.string(),
		arweaveStorageEnabled: types.boolean(),
		// TODO: Remove this field from the schema in all repos.
		/**
		 * Not used. GithubAsSource collection is the source of truth.
		 */
		isGithubAsSourceConnected: types.boolean(),
		// TODO: strict types
		githubAsSourceDetails: types.objectGeneric(types.any()),
		blockBackupAllButton: types.boolean(),
		blockBackupAllDraftsButton: types.boolean(),
		twitterConnectProfile: types.object({
			id: types.string({ required: true }), // ID of the twitter profile
			name: types.string(), // Name on twitter
			username: types.string(), // Twitter handle
			profile_image_url: types.string(), // Twitter profile image
			accessToken: types.string({ required: true }),
			refreshToken: types.string({ required: true }),
		}),
		isHeadless: types.boolean(),
		pro: types.object({
			/**
			 * The user who activated the pro subscription.
			 */
			user: types.objectId({ required: true }),
			/**
			 * Whether or not this publication has access to pro features.
			 */
			hasProAccess: types.boolean({ required: true }),
			/**
			 * The date when the pro subscription cancelation has been requested.
			 */
			canceledAt: types.date(),
			/**
			 * The date when the pro subscription has or will be canceled.
			 */
			cancelationEffectiveDate: types.date(),
			/**
			 * The date when the pro subscription was created.
			 */
			createdAt: types.date(),
			/**
			 * The date when the pro subscription was last updated.
			 */
			updatedAt: types.date({ required: true }),
		}),
		isLegacyPro: types.boolean(),
		disableFooterBranding: types.boolean(),
		isSubscriptionModalDisabled: types.boolean(),
		emailImport: types.object({
			/**
			 * The current data for the ongoing import.
			 * This will be removed for every import.
			 */
			currentImport: types.object({
				importStartedAt: types.date({ required: true }),
				status: types.enum(EMAIL_IMPORT_STATUS, { required: true }),
				successfullyImported: types.number(),
				attemptedToImport: types.number(),
				filename: types.string(),
			}),
		}),
		emailPreferences: types.object({
			draftSubmissionNotifications: types.boolean(),
		}),
		allowContributorEdits: types.boolean(),
		allowCrawlingByGPT: types.boolean(),
	},
	{
		timestamps: true,
		defaults: {
			wwwPrefixedDomainStatus: {
				certIssued: false,
				ready: false,
			},
			numFollowers: 0,
			badgePageEnabled: true,
			membersPageEnabled: true,
			newsletterEnabled: true,
			textSelectionSharerEnabled: true,
			areRoleBasedInviteLinksActive: false,
			newsletterFrequency: 'asap' as const,
			isFeatured: false,
			isActive: true,
			urlPattern: 'simple' as const,
			layout: 'magazine' as const,
			betaFeatures: {},
			views: {
				thisWeek: 0,
				allTime: 0,
				thisMonth: 0,
			},
			viewCountVisible: false,
			readTimeHidden: false,
			customCSSEnabled: false,
			domainChecklistSkipped: false,
			stripe: {
				connected: false,
			},
			umamiDataImported: false,
			isGithubAsSourceConnected: false,
			blockBackupAllButton: false,
			blockBackupAllDraftsButton: false,
			newsletterPageEnabled: true,
			menu: [],
			disableFooterBranding: false,
			isSubscriptionModalDisabled: false,
			emailPreferences: {
				draftSubmissionNotifications: true,
			},
			allowContributorEdits: true,
			allowCrawlingByGPT: false,
		},
	},
)

export const createPublicationIndex = async (db) => {
	await db.createIndex('publications', {
		author: 1,
		isActive: 1,
	})
}

export type PublicationDocument = (typeof publicationSchema)[0]
export type PublicationSchemaOptions = (typeof publicationSchema)[1]
